import { useCallback, useState } from 'react';
import { TakeoverShell } from '../capture/TakeoverShell';
import { MatchesHome } from '../matches/MatchesHome';
import { MoreScreen } from '../more/MoreScreen';
import { MyTeamScreen } from '../myteam/MyTeamScreen';
import { OpponentsScreen } from '../opponents/OpponentsScreen';
import { MatchRecordScreen } from '../record/MatchRecordScreen';
import { SetupScreen } from '../setup/SetupScreen';
import { TabBarB } from './TabBarB';
import type { TabId } from './TabBarB';

/**
 * B spec §2.2 / §2.4 — **the out-of-match shell** (build unit B-5): a conventional tabbed app
 * for everything done with idle hands. Navigation is view state — no URL, no history, no deep
 * links; "back" from a pushed surface is a cleared overlay, i.e. a `from` pointer whose value
 * is always the tab the surface was pushed from.
 *
 * ── What is wired now and what is named for later ────────────────────────────
 *
 * The Matches tab is B-5's; B-6 wired the Opponents tab (list → opponent file, with a match
 * row's tap routed up to the record overlay) and the My Team tab (the roster). **B-19 wired the
 * last one — the More tab — and with it `PlaceholderScreen` was deleted**, per its own rule that
 * the last owning unit takes the component with it:
 *
 * | Tap | Owner |
 * |---|---|
 * | New match | the real setup flow (B-7) |
 * | In-progress card, match under way | the real takeover shell (B-8) |
 * | In-progress card, unstarted setup | the real setup flow (B-7) — see `onOpenSetup` below |
 * | Queue / history / file match row | the real match record (B-18) |
 * | Import an archive | the real More tab (B-19) |
 *
 * B-8 made this shell the takeover's single host: `AppB`'s launch resolve for an in-match
 * surface renders THIS shell opened onto the takeover overlay, so the §2.2 boundary crossing
 * out — `FullTime` (or Abandon) → the tab world, landed on the **match record** for that match
 * — is one overlay swap here on both entry paths (launch resume and the in-progress tap). The
 * chrome has already run its two state-bound syncs on the post-commit render by the time
 * `onLeftMatch` fires (`CaptureChromeB` declares the hand-off effect after them), so the swap
 * is genuinely "only then" (§2.2's ordering).
 *
 * An overlay hides the tab bar: setup and the match record are their own screens (§2.4 lays
 * setup out around the keyboard, which a tab bar would sit inside), and the takeover never
 * shows tabs by definition. The opponent file is NOT an overlay — it is a reading surface the
 * bar stays under, held as view state inside `OpponentsScreen`.
 *
 * `initialOverlay` exists for one caller: `AppB`, whose launch resolve can name the setup
 * surface (§2.2 — a kill mid-setup resumes into setup, not into a dead placeholder). Held as
 * plain initial state; every later overlay change is a tap in this shell.
 */

type Overlay =
  | { readonly kind: 'setup' }
  | { readonly kind: 'record'; readonly matchId: string }
  | {
      readonly kind: 'takeover';
      readonly matchId: string;
      /**
       * B-31/§21.2 — set only by `AppB`'s launch resolve, from `uiSession.surfaceParams`: the
       * adjustments workflow was open at the break when the app was killed. Absent on every
       * in-shell route to the takeover (the Matches tap, and Begin from setup), which is correct —
       * arriving from the tab world is a fresh entry and lands on the break hub's own view, where
       * the entry card now names whatever card already exists (§21.3).
       */
      readonly workflowOpen?: boolean;
    };

export function TabShellB({ initialOverlay = null }: { readonly initialOverlay?: Overlay | null }) {
  const [tab, setTab] = useState<TabId>('matches');
  const [overlay, setOverlay] = useState<Overlay | null>(initialOverlay);

  const closeOverlay = useCallback((): void => {
    setOverlay(null);
  }, []);

  if (overlay !== null) {
    switch (overlay.kind) {
      case 'setup':
        return (
          <SetupScreen
            onClose={closeOverlay}
            onStarted={(matchId) => {
              // PRE-7's exit — the takeover shell is B-8's seam; its placeholder names that.
              setOverlay({ kind: 'takeover', matchId });
            }}
          />
        );
      case 'record':
        return <MatchRecordScreen matchId={overlay.matchId} onClose={closeOverlay} />;
      case 'takeover':
        return (
          <TakeoverShell
            matchId={overlay.matchId}
            initialWorkflowOpen={overlay.workflowOpen === true}
            /* §2.2 — out of the takeover: land on the match record for that match (B-18's
               screen; its placeholder until then). */
            onLeftMatch={(matchId) => {
              setOverlay({ kind: 'record', matchId });
            }}
            /* The row vanished (a cascade elsewhere): nothing to record either — go home. */
            onGone={closeOverlay}
          />
        );
    }
  }

  return (
    <div className="tabshell-b" data-testid="tabshell-b" data-tab={tab}>
      <main className="tabshell-b__content">{renderTab()}</main>
      <TabBarB active={tab} onSelect={setTab} />
    </div>
  );

  function renderTab() {
    switch (tab) {
      case 'matches':
        return (
          <MatchesHome
            onNewMatch={() => {
              setOverlay({ kind: 'setup' });
            }}
            onOpenMatch={(matchId) => {
              setOverlay({ kind: 'takeover', matchId });
            }}
            /* The In-progress card holding an unstarted setup: back into setup, which is where
               `AppB`'s launch resolve has sent the same match since B-7. Before this, both cards
               routed here to the takeover — and for a match that has not kicked off there is
               nothing to capture and no whistle to end, so the only way out was Abandon. */
            onOpenSetup={() => {
              setOverlay({ kind: 'setup' });
            }}
            onOpenRecord={(matchId) => {
              setOverlay({ kind: 'record', matchId });
            }}
            onImportArchive={() => {
              setTab('more');
            }}
          />
        );
      case 'opponents':
        return (
          <OpponentsScreen
            onOpenRecord={(matchId) => {
              setOverlay({ kind: 'record', matchId });
            }}
          />
        );
      case 'myTeam':
        return <MyTeamScreen />;
      case 'more':
        return <MoreScreen />;
    }
  }
}
